"use client";

import { useState } from "react";
import { SAMPLE_POLICIES, STATUS_LABEL, type ReviewStatus } from "./knowledge-sample-data";
import styles from "./KnowledgeWorkspace.module.css";

type StatusFilter = "all" | "needs-review" | ReviewStatus;

export function KnowledgeWorkspace() {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<StatusFilter>("all");
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const visiblePolicies = SAMPLE_POLICIES.filter((policy) => {
    const matchesSearch = !normalizedSearch || [
      policy.name,
      policy.description,
      policy.audience,
      STATUS_LABEL[policy.status],
    ].some((value) => value.toLocaleLowerCase().includes(normalizedSearch));
    const matchesStatus = filter === "all" ||
      (filter === "needs-review" ? policy.status !== "current" : policy.status === filter);
    return matchesSearch && matchesStatus;
  });
  const hasFilters = search.length > 0 || filter !== "all";

  function clearFilters() {
    setSearch("");
    setFilter("all");
  }

  return (
    <figure className={styles.workspace} aria-label="Illustrative Edge knowledge workspace">
      <div className={styles.appHeader}>
        <div>
          <p className={styles.path}>Edge <span aria-hidden="true">/</span> Knowledge Curation <span aria-hidden="true">/</span> Library</p>
          <h3>Communications knowledge</h3>
        </div>
        <span className={styles.exampleLabel}>Illustrative workspace</span>
      </div>

      <div className={styles.toolbar}>
        <p className={styles.resultCount} role="status" aria-live="polite">
          <strong>{visiblePolicies.length}</strong> of {SAMPLE_POLICIES.length} documents
        </p>
        <div className={styles.controls}>
          <label className={styles.searchControl}>
            <span className="sr-only">Search documents</span>
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><circle cx="8.7" cy="8.7" r="5.7" /><path d="m13 13 4 4" /></svg>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search documents"
            />
          </label>
          <label className={styles.filterControl}>
            <span className="sr-only">Filter by review status</span>
            <select value={filter} onChange={(event) => setFilter(event.target.value as StatusFilter)}>
              <option value="all">All review statuses</option>
              <option value="needs-review">Needs review</option>
              <option value="overdue">Overdue</option>
              <option value="conflict">Conflict</option>
              <option value="current">Current</option>
            </select>
          </label>
          {hasFilters && <button className={styles.clearButton} type="button" onClick={clearFilters}>Clear filters</button>}
        </div>
      </div>

      <div className={styles.tableScroll} role="region" aria-label="Knowledge documents" tabIndex={0}>
        <table className={styles.table}>
          <caption className="sr-only">Illustrative knowledge documents with review dates and status</caption>
          <thead>
            <tr>
              <th scope="col">Document</th>
              <th scope="col">Audience</th>
              <th scope="col">Review due</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {visiblePolicies.map((policy) => (
              <tr key={policy.name}>
                <th scope="row"><span className={styles.documentName}>{policy.name}</span><span className={styles.documentDetail}>{policy.description}</span></th>
                <td>{policy.audience}</td>
                <td><time dateTime={policy.due}>{policy.displayDue}</time></td>
                <td><span className={`${styles.status} ${styles[policy.status]}`}>{STATUS_LABEL[policy.status]}</span></td>
              </tr>
            ))}
            {visiblePolicies.length === 0 && (
              <tr><td colSpan={4} className={styles.emptyState}>
                <strong>No documents match these filters.</strong>
                <button type="button" onClick={clearFilters}>Clear filters</button>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.finding}>
        <strong>Library finding · conflicting retention guidance</strong>
        <p>Two policies assign different retention periods to the same support transcripts: 90 days and 365 days. Review the approved rule before either policy is used.</p>
      </div>
      <figcaption>Illustrative policies and findings.</figcaption>
    </figure>
  );
}
